import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Overlay from "../../../Componentes/Overlay";
import Modal from "../../../Componentes/Utils/Modal";
import SmallLoading from "../../../Componentes/Utils/SmallLoading";
import { useModal } from "../../../contexts/ModalContext";
import { useToast } from "../../../contexts/ToastContext";
import useApi from "../../../hooks/useApi";

import { FiSearch, FiTrash2 } from "react-icons/fi";
import { BsArrowLeft } from "react-icons/bs";

const Layout = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #111827;
    text-transform: uppercase;
    letter-spacing: 0.03em;
`;

const Subtitle = styled.p`
    margin: 0;
    font-size: 13px;
    color: #4b5563;
    line-height: 1.45;
`;

const SearchWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 0 12px;
    background: #f9fafb;
    height: 44px;

    & svg {
        color: #6b7280;
        flex-shrink: 0;
    }

    & input {
        flex: 1;
        border: none;
        background: transparent;
        outline: none;
        font-size: 14px;
        color: #1f2937;
    }
`;

const ListWrap = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    max-height: min(60vh, 480px);
    overflow: auto;
`;

const ListItem = styled.button`
    width: 100%;
    background: ${(p) => (p.$active ? "#e0f0ff" : "#ffffff")};
    border: none;
    border-bottom: 1px solid #f3f4f6;
    padding: 12px 14px;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: ${(p) => (p.$active ? "#cfe6ff" : "#f3f4f6")};
    }
`;

const Logo = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e5e7eb;
    background: #f3f4f6;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    & img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    & span {
        font-size: 8px;
        font-weight: 700;
        color: #9ca3af;
        text-transform: uppercase;
        text-align: center;
        padding: 4px;
        line-height: 1.1;
    }
`;

const ObraInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;

    strong {
        font-size: 13px;
        color: #111827;
        line-height: 1.3;
        word-break: break-word;
    }

    small {
        font-size: 12px;
        color: #6b7280;
    }
`;

const Badge = styled.span`
    flex-shrink: 0;
    background: #1f2937;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 999px;
    letter-spacing: 0.02em;
`;

const BackBtn = styled.button`
    background: transparent;
    border: none;
    color: #2563eb;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;

    &:hover {
        text-decoration: underline;
    }
`;

const SectionTitle = styled.h3`
    margin: 0;
    font-size: 0.95rem;
    color: #111827;
    font-weight: 700;
`;

const TwoCol = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 12px;
    background: #ffffff;
    min-height: 0;
`;

const UserRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-bottom: 1px solid #f3f4f6;
    background: ${(p) => (p.$dimmed ? "#f9fafb" : "#ffffff")};

    &:last-child {
        border-bottom: none;
    }
`;

const UserInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;

    strong {
        font-size: 13px;
        color: #111827;
        word-break: break-word;
    }

    small {
        font-size: 12px;
        color: #6b7280;
        word-break: break-all;
    }
`;

const ActionBtn = styled.button`
    border: none;
    padding: 6px 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    color: #ffffff;
    background: ${(p) => (p.$danger ? "#dc2626" : "#2563eb")};
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: background 0.2s;

    &:hover {
        background: ${(p) => (p.$danger ? "#b91c1c" : "#1d4ed8")};
    }

    &:disabled {
        background: #9ca3af;
        cursor: not-allowed;
    }
`;

const EmptyState = styled.p`
    margin: 0;
    padding: 20px;
    text-align: center;
    color: #6b7280;
    font-size: 13px;
`;

const ScrollList = styled.div`
    border: 1px solid #f3f4f6;
    border-radius: 8px;
    max-height: 360px;
    overflow: auto;
`;

const ErrorBox = styled.p`
    margin: 0;
    padding: 12px;
    border-radius: 8px;
    background: #fee2e2;
    color: #991b1b;
    font-size: 13px;
`;

function ObraLogo({ logoUrl }) {
    const url = logoUrl != null && String(logoUrl).trim() !== "" ? String(logoUrl).trim() : null;
    const [broken, setBroken] = useState(false);

    useEffect(() => {
        setBroken(false);
    }, [url]);

    if (!url || broken) {
        return (
            <Logo>
                <span>SEM IMAGEM</span>
            </Logo>
        );
    }

    return (
        <Logo>
            <img src={url} alt="" onError={() => setBroken(true)} />
        </Logo>
    );
}

export default function ModalLiberacaoObras() {
    const { closeModal } = useModal();
    const { notifySuccess, notifyError } = useToast();
    const api = useApi();

    const [obras, setObras] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [loadingBase, setLoadingBase] = useState(true);
    const [erroBase, setErroBase] = useState(null);

    const [obraSelecionada, setObraSelecionada] = useState(null);
    const [acessos, setAcessos] = useState([]);
    const [loadingAcessos, setLoadingAcessos] = useState(false);

    const [filtroObra, setFiltroObra] = useState("");
    const [filtroUsuario, setFiltroUsuario] = useState("");
    const [salvandoUserId, setSalvandoUserId] = useState(null);

    const carregarBase = async () => {
        setLoadingBase(true);
        setErroBase(null);
        try {
            const [obrasResp, usuariosResp] = await Promise.all([
                api.get("/Painel/API/ControleMedicao/ListObrasParaLiberacao/"),
                api.get("/Painel/API/ControleMedicao/ListUsuariosLiberacao/"),
            ]);
            setObras(Array.isArray(obrasResp.data) ? obrasResp.data : []);
            setUsuarios(Array.isArray(usuariosResp.data) ? usuariosResp.data : []);
        } catch (e) {
            console.error(e);
            const status = e?.response?.status;
            if (status === 403) {
                setErroBase("Você não tem permissão para gerenciar a liberação de obras.");
            } else {
                setErroBase(
                    e?.response?.data?.message ||
                        "Não foi possível carregar a lista de obras e usuários."
                );
            }
        } finally {
            setLoadingBase(false);
        }
    };

    useEffect(() => {
        carregarBase();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const carregarAcessos = async (idObra) => {
        setLoadingAcessos(true);
        try {
            const { data } = await api.get(
                `/Painel/API/ControleMedicao/ListAcessosObra/index.php?obra=${idObra}`
            );
            setAcessos(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error(e);
            notifyError(
                e?.response?.data?.message || "Não foi possível carregar os usuários com acesso."
            );
            setAcessos([]);
        } finally {
            setLoadingAcessos(false);
        }
    };

    const selecionarObra = (obra) => {
        setObraSelecionada(obra);
        setFiltroUsuario("");
        carregarAcessos(obra.id);
    };

    const voltarParaObras = () => {
        setObraSelecionada(null);
        setAcessos([]);
        setFiltroUsuario("");
    };

    const obrasFiltradas = useMemo(() => {
        const q = filtroObra.trim().toLowerCase();
        if (!q) return obras;
        return obras.filter((o) => {
            const campos = [o.identificador, o.nome_empresa, o.cidade, o.estado, o.descricao]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return campos.includes(q);
        });
    }, [obras, filtroObra]);

    const usuariosComAcessoMap = useMemo(() => {
        const map = new Map();
        acessos.forEach((a) => {
            map.set(a.id_usuario, a);
        });
        return map;
    }, [acessos]);

    const usuariosFiltrados = useMemo(() => {
        const q = filtroUsuario.trim().toLowerCase();
        if (!q) return usuarios;
        return usuarios.filter((u) => {
            const campos = [u.nome, u.email].filter(Boolean).join(" ").toLowerCase();
            return campos.includes(q);
        });
    }, [usuarios, filtroUsuario]);

    const liberarUsuario = async (usuario) => {
        if (!obraSelecionada) return;
        setSalvandoUserId(usuario.id);
        try {
            const fd = new FormData();
            fd.append("id_obra", String(obraSelecionada.id));
            fd.append("id_usuario", String(usuario.id));
            await api.post("/Painel/API/ControleMedicao/LiberarObraUsuario/", fd);
            await carregarAcessos(obraSelecionada.id);
            await carregarBase();
            notifySuccess(`Acesso liberado para ${usuario.nome}.`);
        } catch (e) {
            console.error(e);
            notifyError(
                e?.response?.data?.message ||
                    "Não foi possível liberar o acesso. Tente novamente."
            );
        } finally {
            setSalvandoUserId(null);
        }
    };

    const revogarUsuario = async (acesso) => {
        if (!obraSelecionada) return;
        if (!window.confirm(`Revogar o acesso de ${acesso.nome} a esta obra?`)) {
            return;
        }
        setSalvandoUserId(acesso.id_usuario);
        try {
            const fd = new FormData();
            fd.append("id_acesso", String(acesso.id_acesso));
            await api.post("/Painel/API/ControleMedicao/RevogarObraUsuario/", fd);
            await carregarAcessos(obraSelecionada.id);
            await carregarBase();
            notifySuccess(`Acesso de ${acesso.nome} revogado.`);
        } catch (e) {
            console.error(e);
            notifyError(
                e?.response?.data?.message ||
                    "Não foi possível revogar o acesso. Tente novamente."
            );
        } finally {
            setSalvandoUserId(null);
        }
    };

    return (
        <Overlay state={true}>
            <Modal header close={closeModal} size="lg">
                <Layout>
                    {obraSelecionada ? (
                        <>
                            <BackBtn type="button" onClick={voltarParaObras}>
                                <BsArrowLeft size={16} />
                                Voltar para a lista de obras
                            </BackBtn>

                            <div>
                                <Title>{obraSelecionada.descricao}</Title>
                                <Subtitle>
                                    Marque os usuários que poderão visualizar esta obra na tela
                                    principal do controle de medição.
                                </Subtitle>
                            </div>

                            <SearchWrap>
                                <FiSearch size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar usuário por nome ou e-mail..."
                                    value={filtroUsuario}
                                    onChange={(e) => setFiltroUsuario(e.target.value)}
                                />
                            </SearchWrap>

                            <TwoCol>
                                <Card>
                                    <SectionTitle>Liberados ({acessos.length})</SectionTitle>
                                    {loadingAcessos ? (
                                        <SmallLoading />
                                    ) : acessos.length === 0 ? (
                                        <EmptyState>
                                            Nenhum usuário liberado para esta obra ainda.
                                        </EmptyState>
                                    ) : (
                                        <ScrollList>
                                            {acessos.map((a) => (
                                                <UserRow key={a.id_acesso}>
                                                    <UserInfo>
                                                        <strong>{a.nome}</strong>
                                                        <small>{a.email}</small>
                                                    </UserInfo>
                                                    <ActionBtn
                                                        type="button"
                                                        $danger
                                                        disabled={salvandoUserId === a.id_usuario}
                                                        onClick={() => revogarUsuario(a)}
                                                        title="Revogar acesso"
                                                    >
                                                        <FiTrash2 size={14} />
                                                        Revogar
                                                    </ActionBtn>
                                                </UserRow>
                                            ))}
                                        </ScrollList>
                                    )}
                                </Card>

                                <Card>
                                    <SectionTitle>
                                        Disponíveis ({usuariosFiltrados.length})
                                    </SectionTitle>
                                    {usuarios.length === 0 ? (
                                        <EmptyState>Nenhum usuário ativo encontrado.</EmptyState>
                                    ) : (
                                        <ScrollList>
                                            {usuariosFiltrados.map((u) => {
                                                const jaLiberado = usuariosComAcessoMap.has(u.id);
                                                return (
                                                    <UserRow key={u.id} $dimmed={jaLiberado}>
                                                        <UserInfo>
                                                            <strong>{u.nome}</strong>
                                                            <small>{u.email}</small>
                                                        </UserInfo>
                                                        <ActionBtn
                                                            type="button"
                                                            disabled={
                                                                jaLiberado ||
                                                                salvandoUserId === u.id
                                                            }
                                                            onClick={() => liberarUsuario(u)}
                                                        >
                                                            {jaLiberado ? "Liberado" : "Liberar"}
                                                        </ActionBtn>
                                                    </UserRow>
                                                );
                                            })}
                                        </ScrollList>
                                    )}
                                </Card>
                            </TwoCol>
                        </>
                    ) : (
                        <>
                            <div>
                                <Title>Liberação de obras</Title>
                                <Subtitle>
                                    Selecione uma obra para gerenciar quais usuários podem
                                    enxergá-la na tela principal do controle de medição.
                                </Subtitle>
                            </div>

                            {erroBase ? (
                                <ErrorBox>{erroBase}</ErrorBox>
                            ) : loadingBase ? (
                                <SmallLoading />
                            ) : (
                                <>
                                    <SearchWrap>
                                        <FiSearch size={16} />
                                        <input
                                            type="text"
                                            placeholder="Buscar obra por identificador, empresa ou cidade..."
                                            value={filtroObra}
                                            onChange={(e) => setFiltroObra(e.target.value)}
                                        />
                                    </SearchWrap>

                                    <ListWrap>
                                        {obrasFiltradas.length === 0 ? (
                                            <EmptyState>
                                                {obras.length === 0
                                                    ? "Nenhuma obra cadastrada."
                                                    : "Nenhuma obra corresponde ao filtro."}
                                            </EmptyState>
                                        ) : (
                                            obrasFiltradas.map((o) => (
                                                <ListItem
                                                    key={o.id}
                                                    type="button"
                                                    onClick={() => selecionarObra(o)}
                                                >
                                                    <ObraLogo logoUrl={o.logo_url} />
                                                    <ObraInfo>
                                                        <strong>{o.descricao}</strong>
                                                        <small>
                                                            {o.cidade} - {o.estado}
                                                        </small>
                                                    </ObraInfo>
                                                    <Badge>
                                                        {o.qtd_usuarios}{" "}
                                                        {o.qtd_usuarios === 1
                                                            ? "usuário"
                                                            : "usuários"}
                                                    </Badge>
                                                </ListItem>
                                            ))
                                        )}
                                    </ListWrap>
                                </>
                            )}
                        </>
                    )}
                </Layout>
            </Modal>
        </Overlay>
    );
}
